import { PLAN_MESSAGE_KIND, planAction, planQuestionType, projectType } from "../../settings/shared/constants";

export const PLAN_APP_TYPE = "PLAN";
export const ASK_APP_TYPE = "ASK";

const SINGLE_SELECT_ALIASES = new Set(["single_select", "single-select", "radio", "select", "choice"]);
const MULTI_SELECT_ALIASES = new Set(["multi_select", "multi-select", "checkbox", "checkboxes", "multiple"]);
const TEXT_ALIASES = new Set(["text", "textarea", "input", "short_text", "long_text"]);

export const isPlanAppType = (value) => String(value || "").toUpperCase() === PLAN_APP_TYPE;
export const isAskAppType = (value) => String(value || "").toUpperCase() === ASK_APP_TYPE;
export const isPlanType = (message) => String(message?.type || "").toUpperCase() === PLAN_APP_TYPE;

export const isPlanQuestionMessage = (message) => {
  if (!message || typeof message !== "object") return false;
  if (Array.isArray(message.planQuestions) && message.planQuestions.length > 0) return true;
  if (message.messageKind === PLAN_MESSAGE_KIND && Array.isArray(message.planQuestions)) {
    return message.planQuestions.length > 0;
  }
  return Boolean(parsePlanMessage(readPlanRaw(message))?.questions?.length);
};

export const isPlanChatMessage = (message) => isPlanQuestionMessage(message);

export const isCompletedPlanResultMessage = (message) => {
  if (!message || message.role === "user") return false;
  if (isPlanQuestionMessage(message)) return false;
  return isPlanType(message) && String(message.content || "").trim().length > 0;
};

export const extractPersistedAnswers = (message) => {
  const candidates = [
    message?.planAnswers,
    message?.answers,
    message?.metadata?.answers,
  ];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate) && Object.keys(candidate).length > 0) {
      return candidate;
    }
  }
  return {};
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
};

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

export const normalizePlanQuestionType = (type) => {
  const raw = String(type || planQuestionType.SINGLE_SELECT).trim().toLowerCase();
  if (MULTI_SELECT_ALIASES.has(raw)) return planQuestionType.MULTI_SELECT;
  if (TEXT_ALIASES.has(raw)) return planQuestionType.TEXT;
  if (SINGLE_SELECT_ALIASES.has(raw)) return planQuestionType.SINGLE_SELECT;
  return raw || planQuestionType.SINGLE_SELECT;
};

export const normalizePlanOption = (option, index = 0) => {
  if (option == null) return null;
  if (typeof option === "string" || typeof option === "number") {
    const label = String(option);
    return { value: label, label, description: "" };
  }
  if (typeof option !== "object") return null;
  const label = option.label ?? option.name ?? option.title ?? option.text ?? option.value ?? `Option ${index + 1}`;
  const value = option.value ?? option.id ?? option.key ?? label;
  return {
    value: String(value),
    label: String(label),
    description: option.description || option.hint || option.subtitle || option.help || "",
  };
};

export const normalizePlanQuestion = (question, index = 0) => {
  if (question == null) return null;
  if (typeof question === "string") {
    return {
      id: `q${index + 1}`,
      question,
      type: planQuestionType.SINGLE_SELECT,
      options: [],
      required: true,
      description: "",
    };
  }
  if (typeof question !== "object") return null;
  const prompt = question.question || question.label || question.title || question.prompt || question.text || "";
  if (!prompt && !asArray(question.options).length) return null;
  const options = asArray(question.options).map(normalizePlanOption).filter(Boolean);
  return {
    id: String(question.id || question.key || `q${index + 1}`),
    question: String(prompt || `Question ${index + 1}`),
    type: normalizePlanQuestionType(question.type),
    options,
    required: question.required !== false,
    description: question.description || question.hint || question.help || "",
  };
};

const extractQuestionsList = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  return data.questions || data.planQuestions || data.items || [];
};

export const parsePlanMessage = (raw) => {
  if (raw == null || raw === "") return null;
  const data = typeof raw === "string" ? tryParseJson(raw) : raw;
  if (data == null) return null;

  if (Array.isArray(data)) {
    const questions = data.map(normalizePlanQuestion).filter(Boolean);
    return questions.length ? { questions, intro: "" } : null;
  }

  if (typeof data !== "object") return null;

  let questions = extractQuestionsList(data);
  if (!Array.isArray(questions) || questions.length === 0) {
    if (data.question || data.type) {
      const single = normalizePlanQuestion(data, 0);
      questions = single ? [single] : [];
    }
  } else {
    questions = questions.map(normalizePlanQuestion).filter(Boolean);
  }

  if (!questions.length) return null;

  return {
    questions,
    intro: data.intro || data.message || data.title || data.heading || data.prompt || "",
  };
};

const PLAN_RAW_KEYS = [
  "planMessage",
  "plan_message",
  "planQuestions",
  "plan_questions",
];

const readPlanRaw = (source) => {
  if (!source || typeof source !== "object") return null;
  for (const key of PLAN_RAW_KEYS) {
    if (source[key] != null && source[key] !== "") return source[key];
  }
  if (source.metadata && typeof source.metadata === "object") {
    for (const key of PLAN_RAW_KEYS) {
      if (source.metadata[key] != null && source.metadata[key] !== "") return source.metadata[key];
    }
  }
  if (source.msg && typeof source.msg === "object") {
    for (const key of PLAN_RAW_KEYS) {
      if (source.msg[key] != null && source.msg[key] !== "") return source.msg[key];
    }
  }
  return null;
};

export const extractPlanMessageFromWs = (message) => {
  if (!message || typeof message !== "object") return null;
  const ninfo = message.ninfo && typeof message.ninfo === "object" ? message.ninfo : {};
  const candidates = [
    readPlanRaw(ninfo),
    readPlanRaw(message),
    readPlanRaw(ninfo.msg),
    readPlanRaw(ninfo.data),
    readPlanRaw(message.data),
  ];
  for (const candidate of candidates) {
    const parsed = parsePlanMessage(candidate);
    if (parsed) return parsed;
  }
  return null;
};

export const getPlanFingerprint = (questions = [], extra = "") => {
  // Include question text + option values so a later PLAN round with the same
  // ids (q1/q2/…) does not reuse another round's localStorage answers.
  const parts = (questions || []).map((q) => {
    if (!q || typeof q !== "object") return "";
    const opts = (q.options || [])
      .map((opt) => String(opt?.value ?? ""))
      .filter(Boolean)
      .join(",");
    return `${q.id || ""}:${String(q.question || "").trim()}:${q.type || ""}:${opts}`;
  });
  return `${parts.join("|")}::${extra || ""}`;
};

/** Keep only answers that still match the current question options / type. */
export const sanitizePlanAnswers = (questions = [], answers = {}) => {
  const cleaned = {};
  if (!answers || typeof answers !== "object") return cleaned;
  (questions || []).forEach((question) => {
    if (!question?.id) return;
    const raw = answers[question.id];
    if (raw == null || raw === "") return;
    const optionValues = new Set(
      (question.options || []).map((opt) => String(opt.value))
    );
    const hasOptions = optionValues.size > 0;

    if (question.type === planQuestionType.MULTI_SELECT) {
      const list = (Array.isArray(raw) ? raw : [raw])
        .map((item) => String(item))
        .filter((item) => item && (!hasOptions || optionValues.has(item)));
      if (list.length) cleaned[question.id] = list;
      return;
    }

    if (
      question.type === planQuestionType.SINGLE_SELECT ||
      (hasOptions && question.type !== planQuestionType.TEXT)
    ) {
      const value = Array.isArray(raw) ? raw[0] : raw;
      if (value != null && String(value) && (!hasOptions || optionValues.has(String(value)))) {
        cleaned[question.id] = value;
      }
      return;
    }

    // Free-text: only restore if it looks like a real typed answer for THIS round
    // (never restore multi-select blobs into text fields).
    if (typeof raw === "string" && raw.trim()) {
      cleaned[question.id] = raw;
    }
  });
  return cleaned;
};

export const isPlanQuestionAnswered = (question, value) => {
  if (!question?.required) return true;
  const hasOptions = (question.options || []).length > 0;
  if (
    (question.type === planQuestionType.SINGLE_SELECT || question.type === planQuestionType.MULTI_SELECT) &&
    !hasOptions
  ) {
    return true;
  }
  if (question.type === planQuestionType.MULTI_SELECT) {
    return Array.isArray(value) && value.length > 0;
  }
  if (question.type === planQuestionType.TEXT) {
    return String(value || "").trim().length > 0;
  }
  return value != null && String(value).trim() !== "";
};

export const getOptionLabel = (question, value) => {
  const match = (question?.options || []).find((opt) => String(opt.value) === String(value));
  return match?.label || (value == null ? "" : String(value));
};

export const formatPlanAnswersSummary = (questions = [], answers = {}) =>
  (questions || [])
    .map((question) => {
      const value = answers[question.id];
      if (Array.isArray(value)) {
        const labels = value.map((item) => getOptionLabel(question, item)).filter(Boolean);
        return labels.length ? `${question.question}: ${labels.join(", ")}` : null;
      }
      const label = getOptionLabel(question, value);
      return label ? `${question.question}: ${label}` : null;
    })
    .filter(Boolean)
    .join("\n");

export const areAllRequiredPlanQuestionsAnswered = (questions = [], answers = {}) =>
  (questions || []).every((question) => isPlanQuestionAnswered(question, answers[question.id]));

export const getFirstUnansweredPlanIndex = (questions = [], answers = {}) => {
  const index = (questions || []).findIndex((question) => !isPlanQuestionAnswered(question, answers[question.id]));
  if (index < 0) return Math.max((questions || []).length - 1, 0);
  return index;
};

export const countAnsweredPlanQuestions = (questions = [], answers = {}) =>
  (questions || []).filter((question) => {
    const value = answers[question.id];
    if (Array.isArray(value)) return value.length > 0;
    return value != null && String(value).trim() !== "";
  }).length;

/** Convert selected answers into the Plan `userPrompt` sent to the backend. */
export const buildPlanUserPrompt = (questions = [], answers = {}, additionalInput = "") => {
  const blocks = (questions || []).map((question) => {
    const value = answers[question.id];
    const isMulti = question.type === planQuestionType.MULTI_SELECT || Array.isArray(value);
    const labels = Array.isArray(value)
      ? value.map((item) => getOptionLabel(question, item)).filter(Boolean)
      : [getOptionLabel(question, value)].filter(Boolean);
    if (!labels.length) return null;
    const heading = isMulti || labels.length > 1 ? "Answers:" : "Answer:";
    const body = isMulti || labels.length > 1 ? labels.join(",\n") : labels[0];
    return `Question:\n${question.question}\n\n${heading}\n${body}`;
  }).filter(Boolean);

  const extra = String(additionalInput || "").trim();
  if (extra) {
    blocks.push(`Additional input:\n${extra}`);
  }
  return blocks.join("\n\n");
};

export const getQuestionIdFingerprint = (questions = []) => getPlanFingerprint(questions, "");

const planProgressKey = (projectId, fingerprint) =>
  `sa-plan-progress:${projectId || "new"}:${fingerprint || "unknown"}`;

export const readPlanProgress = (projectId, message) => {
  const questions = message?.planQuestions || parsePlanMessage(readPlanRaw(message))?.questions || [];
  const fingerprint = message?.planFingerprint || getQuestionIdFingerprint(questions);
  const keys = [
    message?.id ? planProgressKey(projectId, String(message.id)) : null,
    fingerprint ? planProgressKey(projectId, fingerprint) : null,
  ].filter(Boolean);
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const answers = sanitizePlanAnswers(
          questions,
          parsed.answers && typeof parsed.answers === "object" ? parsed.answers : {}
        );
        const hasValidAnswers = Object.keys(answers).length > 0;
        return {
          answers,
          additionalInput: hasValidAnswers ? parsed.additionalInput || "" : "",
          // Never mark submitted when restored answers don't fit these questions.
          submitted: Boolean(parsed.submitted) && hasValidAnswers,
        };
      }
    } catch {
      /* ignore */
    }
  }
  return { answers: {}, additionalInput: "", submitted: false };
};

export const writePlanProgress = (projectId, message, { answers, additionalInput, submitted } = {}) => {
  const questions = message?.planQuestions || [];
  const fingerprint = message?.planFingerprint || getQuestionIdFingerprint(questions);
  const payload = JSON.stringify({
    answers: answers || {},
    additionalInput: additionalInput || "",
    submitted: Boolean(submitted),
    updatedAt: Date.now(),
  });
  const keys = [
    message?.id ? planProgressKey(projectId, String(message.id)) : null,
    fingerprint ? planProgressKey(projectId, fingerprint) : null,
  ].filter(Boolean);
  keys.forEach((key) => {
    try {
      localStorage.setItem(key, payload);
    } catch {
      /* ignore */
    }
  });
};

/** `{ q1: "api_testing", q2: "python", q3: ["ui_interface", "reporting"] }` */
export const toPlanAnswerMap = (questions = [], answers = {}) => {
  const map = {};
  (questions || []).forEach((question) => {
    const raw = answers[question.id];
    if (question.type === planQuestionType.MULTI_SELECT) {
      if (Array.isArray(raw)) {
        map[question.id] = raw.map((item) => String(item));
      } else if (raw != null && raw !== "") {
        map[question.id] = [String(raw)];
      } else {
        map[question.id] = [];
      }
      return;
    }
    if (raw != null && raw !== "") {
      map[question.id] = raw;
    }
  });
  return map;
};

export const composerModeStorageKey = (projectId) =>
  `sa-chat-composer-mode:${projectId || "new"}`;

const COMPOSER_MODE_VALUES = new Set(["APP", "APP_AGENT", "AGENT", "PLAN", "WEBSITE", "ASK"]);

export const PLAN_WORKSPACE_MODE_EVENT = "sa-plan-workspace-mode";
export const PLAN_CHAT_MESSAGES_EVENT = "sa-plan-chat-messages";
export const PLAN_WORKSPACE_BUILD_KEY = (projectId) =>
  `sa-plan-workspace-build:${projectId || "new"}`;
export const PLAN_WORKSPACE_VIEW_KEY = (projectId) =>
  `sa-plan-workspace-view:${projectId || "new"}`;
export const PLAN_META_KEY = (projectId) => `sa-plan-meta:${projectId || "new"}`;

export const readPlanMeta = (projectId) => {
  try {
    const raw = sessionStorage.getItem(PLAN_META_KEY(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

export const writePlanMeta = (projectId, patch = {}) => {
  try {
    const prev = readPlanMeta(projectId) || {};
    const next = {
      ...prev,
      ...patch,
      updatedAt: Date.now(),
    };
    sessionStorage.setItem(PLAN_META_KEY(projectId), JSON.stringify(next));
    try {
      window.dispatchEvent(
        new CustomEvent(PLAN_WORKSPACE_MODE_EVENT, {
          detail: { projectId, planMeta: next },
        })
      );
    } catch {
      /* ignore */
    }
    return next;
  } catch {
    return null;
  }
};
export const readComposerMode = (projectId) => {
  try {
    const value = sessionStorage.getItem(composerModeStorageKey(projectId));
    return value && COMPOSER_MODE_VALUES.has(value) ? value : "";
  } catch {
    return "";
  }
};

export const writeComposerMode = (projectId, value) => {
  try {
    const normalized = String(value || "").toUpperCase();
    if (!normalized || !COMPOSER_MODE_VALUES.has(normalized)) {
      sessionStorage.removeItem(composerModeStorageKey(projectId));
      return;
    }
    sessionStorage.setItem(composerModeStorageKey(projectId), normalized);
    if (normalized === PLAN_APP_TYPE) {
      writePlanWorkspaceBuildMode(projectId, false);
    } else {
      writePlanWorkspaceBuildMode(projectId, true);
    }
    try {
      window.dispatchEvent(
        new CustomEvent(PLAN_WORKSPACE_MODE_EVENT, {
          detail: { projectId, mode: normalized },
        })
      );
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
};

export const readPlanWorkspaceBuildMode = (projectId) => {
  try {
    return sessionStorage.getItem(PLAN_WORKSPACE_BUILD_KEY(projectId)) === "1";
  } catch {
    return false;
  }
};

export const writePlanWorkspaceBuildMode = (projectId, enabled) => {
  try {
    const key = PLAN_WORKSPACE_BUILD_KEY(projectId);
    if (enabled) {
      sessionStorage.setItem(key, "1");
      writePlanWorkspaceViewOpen(projectId, false);
    } else {
      sessionStorage.removeItem(key);
    }
    try {
      window.dispatchEvent(
        new CustomEvent(PLAN_WORKSPACE_MODE_EVENT, {
          detail: {
            projectId,
            mode: enabled ? "APP" : "PLAN",
            buildMode: Boolean(enabled),
          },
        })
      );
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
};

/** Explicit "View Plan" while composer may stay on APP / APP_AGENT. */
export const readPlanWorkspaceViewOpen = (projectId) => {
  try {
    return sessionStorage.getItem(PLAN_WORKSPACE_VIEW_KEY(projectId)) === "1";
  } catch {
    return false;
  }
};

export const writePlanWorkspaceViewOpen = (projectId, open) => {
  try {
    const key = PLAN_WORKSPACE_VIEW_KEY(projectId);
    if (open) {
      sessionStorage.setItem(key, "1");
      sessionStorage.removeItem(PLAN_WORKSPACE_BUILD_KEY(projectId));
    } else {
      sessionStorage.removeItem(key);
    }
    try {
      window.dispatchEvent(
        new CustomEvent(PLAN_WORKSPACE_MODE_EVENT, {
          detail: {
            projectId,
            viewOpen: Boolean(open),
            buildMode: open ? false : readPlanWorkspaceBuildMode(projectId),
          },
        })
      );
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
};

/** True when project already has a living plan document to open in the UI. */
export const hasExistingLivingPlan = (projectData, projectId = null, messages = []) => {
  const id = projectId || projectData?.id;
  const meta = readPlanMeta(id) || {};
  const planText = String(
    projectData?.plan?.planText ||
      projectData?.planText ||
      projectData?.plan_text ||
      meta.planText ||
      ""
  ).trim();
  const planId = resolvePlanId(projectData, messages?.length ? messages : projectData?.messages || []);
  return Boolean(planId && planText);
};

export const resolvePlanId = (projectData, messages = []) => {
  const meta = readPlanMeta(projectData?.id);
  const fromMessages = (messages || []).find((msg) => msg?.planId || msg?.metadata?.planId);
  return (
    projectData?.plan?.planId ||
    projectData?.plan?.id ||
    projectData?.planId ||
    projectData?.plan_id ||
    projectData?.metadata?.planId ||
    projectData?.metadata?.plan_id ||
    projectData?.projectInfo?.planId ||
    meta?.planId ||
    fromMessages?.planId ||
    fromMessages?.metadata?.planId ||
    (isPlanAppType(projectData?.appType ?? projectData?.app_type) ? projectData?.id : null) ||
    null
  );
};

export const resolveBuildAvailable = (projectData, messages = [], options = {}) => {
  const {
    planAwaitingResponse = false,
    planStatus = null,
    isGenerating = false,
  } = options || {};

  // Latest generation state wins — never show Ready/Build from a previous plan
  // while a new planning turn is in progress or failed.
  const status = String(
    planStatus ||
      projectData?.plan?.planStatus ||
      projectData?.plan?.status ||
      projectData?.metadata?.planStatus ||
      readPlanMeta(projectData?.id)?.planStatus ||
      ""
  ).toLowerCase();
  if (
    planAwaitingResponse ||
    isGenerating ||
    status === "updating" ||
    status === "planning" ||
    status === "failed"
  ) {
    return false;
  }

  const meta = readPlanMeta(projectData?.id);
  const explicit =
    meta?.buildAvailable ??
    projectData?.buildAvailable ??
    projectData?.build_available ??
    projectData?.metadata?.buildAvailable ??
    projectData?.metadata?.build_available ??
    projectData?.projectInfo?.buildAvailable;
  if (explicit === true || explicit === "true" || explicit === 1) return true;
  if (explicit === false || explicit === "false" || explicit === 0) return false;

  // Living plan already in DB / session — treat as Ready so Build / View Plan work
  // after reload without relying on chat message flags.
  const livingText = String(
    projectData?.plan?.planText ||
      projectData?.planText ||
      meta?.planText ||
      ""
  ).trim();
  if (
    livingText &&
    (status === "ready" ||
      status === "completed" ||
      status === "success" ||
      status === "" ||
      Boolean(resolvePlanId(projectData, messages)))
  ) {
    return true;
  }

  return (messages || []).some(
    (msg) =>
      msg?.buildAvailable === true ||
      msg?.metadata?.buildAvailable === true ||
      (isCompletedPlanResultMessage(msg) && Boolean(msg?.planId || msg?.metadata?.planId || meta?.planId))
  );
};

/** Prefer live WS plan, then session meta, then persisted project, then chat. */
export const resolvePlanBuildPrompt = (projectData, messages = [], preferredPlanText = null) => {
  const meta = readPlanMeta(projectData?.id);
  const stripMarkers = (text) =>
    String(text || "")
      .replace(/^\s*BEGIN_PLAN\s*/i, "")
      .replace(/\s*END_PLAN\s*$/i, "")
      .trim();

  // Prefer the newest looking plan text. After a refine, localStorage meta /
  // project metadata often has the updated body while preferredPlanText can
  // still be the pre-refine document snapshot.
  const fromPreferred = stripMarkers(preferredPlanText);
  const fromMeta = stripMarkers(meta?.planText);
  if (fromPreferred && fromMeta && fromPreferred !== fromMeta) {
    return fromMeta.length >= fromPreferred.length ? fromMeta : fromPreferred;
  }
  if (fromPreferred) return fromPreferred;
  if (fromMeta) return fromMeta;

  const fromProject = stripMarkers(
    projectData?.plan?.planText ||
      projectData?.planText ||
      projectData?.plan_text ||
      projectData?.metadata?.planText
  );
  if (fromProject) return fromProject;

  const completed = [...(messages || [])]
    .reverse()
    .find((msg) => isCompletedPlanResultMessage(msg) || (msg?.role === "assistant" && msg?.planText));
  const fromMessage = stripMarkers(completed?.planText || completed?.content);
  if (fromMessage) return fromMessage;

  const source = [...(messages || [])]
    .reverse()
    .find((msg) => msg?.planSourcePrompt || (msg?.role === "user" && (msg?.prompt || msg?.content)));
  return String(source?.planSourcePrompt || source?.prompt || source?.content || "").trim();
};

/**
 * Reuse the existing APP/resume request shape.
 * Only difference from PLAN: type/appType become APP (via RESUME + appType APP).
 * Do NOT embed plan markdown in the user prompt — plan travels via metadata /
 * APPROVED_PLAN.md on the worker (avoids huge cache write/read).
 */
export const buildAppRequestFromPlan = ({ projectId, prompt, appType = "APP" } = {}) => {
  const userTyped = String(prompt || "").trim();
  // Kickoff may pass plan body historically — never ship that as user_prompt.
  const looksLikePlan =
    /^#\s+/m.test(userTyped) ||
    /##\s+(Overview|Objective|Scope|Key Features|Implementation Plan)/i.test(userTyped);
  const userPrompt =
    userTyped && !looksLikePlan
      ? userTyped
      : "Start building the approved plan now. Implement the MVP end-to-end.";
  const normalizedAppType = String(appType || "APP").toUpperCase();
  return {
    projectId,
    type: projectType.RESUME,
    prompt: userPrompt,
    appType:
      normalizedAppType === "APP_AGENT" || normalizedAppType === "WEBSITE"
        ? normalizedAppType
        : "APP",
  };
};
export const isProjectInPlanMode = (projectData, projectId) => {
  const id = projectId || projectData?.id;
  // Explicit View Plan — show workspace even if composer is APP / APP_AGENT.
  if (readPlanWorkspaceViewOpen(id)) return true;
  if (readPlanWorkspaceBuildMode(id)) return false;
  const composer = readComposerMode(id);
  if (composer) return composer === PLAN_APP_TYPE;
  if (isPlanAppType(projectData?.appType ?? projectData?.app_type)) return true;
  // Existing living plan after reload: open Plan Workspace by default until Build.
  if (hasExistingLivingPlan(projectData, id)) return true;
  const messages = projectData?.messages || [];
  return messages.some((msg) => isPlanChatMessage(msg) && msg.planStatus !== "submitted");
};

const SECTION_ALIASES = [
  { key: "overview", match: /^(?:\d+\.\s*)?(?:project\s+)?overview|summary|understanding|project\s+understanding$/i },
  { key: "objective", match: /^(?:\d+\.\s*)?(?:objective|goal|goals|purpose)$/i },
  { key: "scope", match: /^(?:\d+\.\s*)?(?:scope|in\s*scope|out\s*of\s*scope)$/i },
  { key: "features", match: /^(?:\d+\.\s*)?(?:(?:key\s+)?features|feature\s+set|core\s+features)$/i },
  { key: "users", match: /^(?:\d+\.\s*)?(?:users?(\s*&\s*|\s+and\s+)?roles?|roles?)$/i },
  { key: "screens", match: /^(?:\d+\.\s*)?(?:screens?(\s*\/\s*pages)?(\s*&\s*(user\s+)?flows)?|pages?(\s*&\s*(user\s+)?flows)?|user\s+flows?)$/i },
  { key: "architecture", match: /^(?:\d+\.\s*)?(?:(?:system[- ]?level\s+)?architecture|system\s+architecture|data\s+flow|agent\s+flow)$/i },
  { key: "erd", match: /^(?:\d+\.\s*)?(?:database(\s*\/\s*erd)?|erd|data\s+model|entity(\s+relationship)?(\s+diagram)?)$/i },
  { key: "techStack", match: /^(?:\d+\.\s*)?(?:(?:technology|tech)\s*stack|integrations?\s*\/\s*stack|stack|technologies)$/i },
  { key: "agents", match: /^(?:\d+\.\s*)?(?:agents?(\s*\/\s*ai\s+components?)?|ai\s+components?|orchestrator)$/i },
  { key: "milestones", match: /^(?:\d+\.\s*)?(?:implementation(\s+plan)?(\s*\/\s*milestones)?|milestones?|tasks?|implementation\s+phases?|delivery\s+(?:sequence|plan|order)|build\s+sequence)$/i },
  { key: "security", match: /^(?:\d+\.\s*)?security(\s+considerations?)?$/i },
  { key: "assumptions", match: /^(?:\d+\.\s*)?assumptions?$/i },
  { key: "risks", match: /^(?:\d+\.\s*)?(?:risks?(\s*\/\s*considerations?)?|considerations?|acceptance\s+criteria)$/i },
  { key: "creditEstimation", match: /^(?:\d+\.\s*)?(?:credit(?:s)?(?:\s+estimation)?|estimated?\s+credits?|cost(?:\s+estimation)?|token(?:\s+estimation)?)$/i },
];

const emptyPlanDocument = () => ({
  overview: "",
  objective: "",
  scope: { included: [], excluded: [] },
  features: [],
  users: [],
  screens: [],
  architecture: [],
  architectureMermaid: "",
  architectureNotes: "",
  architectureModules: [],
  extraMermaid: [],
  erdMermaid: "",
  erdNotes: "",
  techStack: [],
  agents: [],
  agentsNote: "",
  milestones: [],
  security: [],
  assumptions: [],
  risks: [],
  creditEstimation: "",
  rawSections: {},
});

const stripInlineMd = (value = "") =>
  String(value)
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/`/g, "")
    .replace(/^#+\s*/, "")
    .trim();

const unescapePlanMarkup = (text = "") =>
  String(text || "")
    .replace(/\\`/g, "`")
    .replace(/&#96;/gi, "`")
    .replace(/&grave;/gi, "`");

const MERMAID_DIAGRAM_RE =
  /^(flowchart|graph|erDiagram|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|gantt|pie|mindmap|gitGraph|journey|quadrantChart|timeline)\b/i;

const isMermaidFenceLine = (line = "") => /^[`~]{2,}\s*(mermaid)?\s*$/i.test(String(line).trim());

const isMermaidProseLine = (trimmed = "") => {
  if (!trimmed) return false;
  if (MERMAID_DIAGRAM_RE.test(trimmed) || isMermaidFenceLine(trimmed)) return false;
  if (/-->|\.\.\.|---|==>|\|\|--|::/.test(trimmed)) return false;
  if (/[{}\[\]|]/.test(trimmed) && trimmed.length < 90) return false;
  return /^[A-Z]/.test(trimmed) && trimmed.length > 40 && /[.!?]/.test(trimmed);
};

const isMermaidDebrisLine = (line = "") => {
  const trimmed = String(line).trim();
  if (!trimmed) return false;
  if (isMermaidFenceLine(trimmed) || /^mermaid$/i.test(trimmed)) return true;
  if (MERMAID_DIAGRAM_RE.test(trimmed.replace(/^[`~]{2,}\s*/, ""))) return true;
  return /-->|\|\|--|^\s*[A-Za-z0-9_]\s*[\[{(]/.test(trimmed);
};

const classifyMermaidCharts = (charts = []) => {
  const architecture = [];
  const erd = [];
  const extra = [];
  charts.forEach((chart) => {
    const start = String(chart || "").trim();
    if (/^erDiagram\b/i.test(start)) erd.push(start);
    else if (/^(flowchart|graph)\b/i.test(start)) architecture.push(start);
    else extra.push(start);
  });
  return { architecture, erd, extra };
};

const extractFencedMermaid = (text = "") => {
  const charts = [];
  const remainder = String(text).replace(
    /(?:^|\n)[ \t]*([`~]{2,})\s*(mermaid)?[ \t]*\r?\n([\s\S]*?)(?:\n[ \t]*\1[ \t]*)(?=\n|$)/gi,
    (match, _fence, lang, body) => {
      const cleaned = String(body || "").trim();
      if (!cleaned) return match;
      if (lang || MERMAID_DIAGRAM_RE.test(cleaned)) {
        charts.push(cleaned);
        return "\n";
      }
      return match;
    }
  );
  return { charts, remainder };
};

const extractUnfencedMermaid = (text = "") => {
  const charts = [];
  const kept = [];
  let collecting = null;
  const stop = () => {
    if (collecting?.length) {
      const chart = collecting.join("\n").trim();
      if (chart) charts.push(chart);
    }
    collecting = null;
  };

  String(text)
    .split(/\n/)
    .forEach((line) => {
      const trimmed = line.trim();
      if (collecting) {
        if (isMermaidFenceLine(trimmed) || /^#{1,6}\s/.test(trimmed)) {
          stop();
          if (/^#{1,6}\s/.test(trimmed)) kept.push(line);
          return;
        }
        if (isMermaidProseLine(trimmed)) {
          stop();
          kept.push(line);
          return;
        }
        collecting.push(line);
        return;
      }
      if (isMermaidFenceLine(trimmed)) return;
      const start = trimmed.replace(/^[`~]{2,}\s*/, "");
      if (MERMAID_DIAGRAM_RE.test(start)) {
        collecting = [start];
        return;
      }
      kept.push(line);
    });
  stop();
  return { charts, remainder: kept.join("\n") };
};

const extractMermaidBlocks = (text = "") => {
  const source = unescapePlanMarkup(text);
  const fenced = extractFencedMermaid(source);
  const unfenced = extractUnfencedMermaid(fenced.remainder);
  return {
    charts: [...fenced.charts, ...unfenced.charts].filter(Boolean),
    remainder: unfenced.remainder.trim(),
  };
};

export const resolveArchitectureChart = (doc = {}) => {
  if (doc.architectureMermaid) return doc.architectureMermaid;
  const blob = [doc.architectureNotes, ...(doc.architecture || []), doc.rawSections?.architecture || ""]
    .filter(Boolean)
    .join("\n");
  const { architecture, extra } = classifyMermaidCharts(extractMermaidBlocks(blob).charts);
  return architecture[0] || extra[0] || "";
};

export const resolveErdChart = (doc = {}) => {
  if (doc.erdMermaid) return doc.erdMermaid;
  const blob = [doc.erdNotes, doc.rawSections?.erd || "", doc.rawSections?.["Database / ERD"] || ""]
    .filter(Boolean)
    .join("\n");
  const { erd } = classifyMermaidCharts(extractMermaidBlocks(blob).charts);
  return erd[0] || "";
};

const bulletLines = (text = "") =>
  String(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line && !/^#{1,6}\s/.test(line) && !/^```/.test(line))
    .map((line) => stripInlineMd(line.replace(/^[\s>*\-•\d.)]+/, "")))
    .filter((line) => line && !/^(included|excluded)$/i.test(line));

const parseScopeBlock = (text = "") => {
  const included = [];
  const excluded = [];
  let bucket = "included";
  String(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const heading = line.replace(/^#{1,6}\s+/, "").replace(/[:：]\s*$/, "").trim();
      if (/^(included|in\s*scope)$/i.test(heading)) {
        bucket = "included";
        return;
      }
      if (/^(excluded|out\s*of\s*scope|not\s+included)$/i.test(heading)) {
        bucket = "excluded";
        return;
      }
      const clean = stripInlineMd(line.replace(/^[\s>*\-•\d.)✓✔✕×]+/, ""));
      if (!clean) return;
      if (/✕|×|✗/.test(line) || /^(out\s*of\s*scope|excluded|not\s+included)/i.test(line)) {
        excluded.push(clean.replace(/^(out\s*of\s*scope|excluded|not\s+included)\s*[:\-–]?\s*/i, ""));
        return;
      }
      (bucket === "excluded" ? excluded : included).push(clean);
    });
  return { included, excluded };
};

const parseTechStack = (text = "") => {
  const items = [];
  String(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line && !/^#{1,6}\s/.test(line) && !/^```/.test(line))
    .forEach((line) => {
      const clean = stripInlineMd(line.replace(/^[\s>*\-•\d.)]+/, ""));
      if (!clean) return;
      const parts = clean.split(/[:：]/);
      if (parts.length >= 2) {
        items.push({ label: parts[0].trim(), value: parts.slice(1).join(":").trim() });
      } else {
        items.push({ label: "Tech", value: clean });
      }
    });
  return items;
};

const parseAgents = (text = "") => {
  const { remainder } = extractMermaidBlocks(text);
  const lines = bulletLines(remainder);
  if (!lines.length) {
    return { agents: [], note: remainder ? stripInlineMd(remainder) : "" };
  }
  const agents = lines.map((line, index) => {
    const parts = line.split(/\s+[—–\-]\s+|[:：]/);
    if (parts.length >= 2) {
      const rest = parts.slice(1).join(" — ").trim();
      const toolMatch = rest.match(/\btools?\s*[:：]\s*(.+)$/i);
      return {
        id: `agent-${index}`,
        name: parts[0].trim(),
        responsibility: toolMatch ? rest.replace(toolMatch[0], "").trim() : rest,
        tools: toolMatch ? toolMatch[1].split(/[,/]/).map((item) => item.trim()).filter(Boolean) : [],
      };
    }
    return { id: `agent-${index}`, name: line, responsibility: "", tools: [] };
  });
  return { agents, note: "" };
};

const normalizeTaskStatus = (value = "") => {
  const raw = String(value).toLowerCase();
  if (/complet|done|finished/.test(raw)) return "completed";
  if (/fail/.test(raw)) return "failed";
  if (/block/.test(raw)) return "blocked";
  if (/test/.test(raw)) return "testing";
  if (/progress|current|active/.test(raw)) return "current";
  return "pending";
};

const cleanPhaseTitle = (value = "") =>
  stripInlineMd(value)
    .replace(/^\d+\.?\s*/, "")
    .replace(/\((week|day|sprint)[^)]*\)/gi, "")
    .replace(/\bweek(s)?\s*\d+(\s*-\s*\d+)?\b/gi, "")
    .replace(/\b\d+\s*-\s*\d+\s*weeks?\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const parseMilestones = (text = "") => {
  const { remainder } = extractMermaidBlocks(text);
  const lines = String(remainder).split(/\n/);
  const phases = [];
  let current = null;
  const pushCurrent = () => {
    if (current) phases.push(current);
  };

  lines.forEach((line) => {
    const heading = line.match(/^#{3,6}\s+(.+)$/);
    if (heading) {
      pushCurrent();
      current = {
        title: cleanPhaseTitle(heading[1]),
        status: "pending",
        tasks: [],
      };
      return;
    }
    if (!current) {
      const numbered = line.match(/^\s*\d+\.\s+(.+)/);
      if (numbered) {
        pushCurrent();
        current = {
          title: cleanPhaseTitle(numbered[1]),
          status: "pending",
          tasks: [],
        };
        return;
      }
    }
    if (!current) return;
    const status = line.match(/^\s*status\s*[:：]\s*(.+)$/i);
    if (status) {
      current.status = normalizeTaskStatus(status[1]);
      return;
    }
    const bullet = line.match(/^\s*[-*•]\s+(.+)/);
    if (bullet) {
      const task = stripInlineMd(bullet[1]);
      if (task) current.tasks.push(task);
    }
  });
  pushCurrent();

  if (phases.length) {
    return phases
      .filter((phase) => phase.title)
      .map((phase, index) => ({
        id: `m-${index}`,
        title: phase.title,
        detail: phase.tasks.join("\n"),
        tasks: phase.tasks,
        status: phase.status || "pending",
      }));
  }

  return bulletLines(remainder)
    .filter((line) => !/^status\s*:/i.test(line))
    .map((line, index) => ({
      id: `m-${index}`,
      title: cleanPhaseTitle(line) || line,
      detail: "",
      tasks: [],
      status: "pending",
    }));
};

const parseArchitecture = (text = "") => {
  const { charts, remainder } = extractMermaidBlocks(text);
  const classified = classifyMermaidCharts(charts);
  const notes = remainder
    .split(/\n+/)
    .map((line) => stripInlineMd(line.replace(/^[\s>*\-•]+/, "")))
    .filter((line) => line && !/^#{1,6}/.test(line) && !isMermaidDebrisLine(line));
  const hasMermaid = charts.length > 0;
  let nodes = notes;
  if (!hasMermaid) {
    const arrows = notes
      .flatMap((line) => line.split(/→|➜|⇒/))
      .map((part) => stripInlineMd(part))
      .filter(Boolean);
    if (arrows.length > 1) nodes = arrows;
  }
  return {
    mermaid: classified.architecture[0] || (!classified.erd[0] ? charts[0] : "") || "",
    extra: [...classified.architecture.slice(1), ...classified.extra],
    erd: classified.erd[0] || "",
    notes: notes.join("\n"),
    nodes,
  };
};

const splitMarkdownByHeading = (text = "", level = 3) => {
  const sections = [];
  let current = null;
  const headingRe = new RegExp(`^#{${level}}(?!#)\\s+(.+)$`);
  String(text)
    .split(/\n/)
    .forEach((line) => {
      const match = line.match(headingRe);
      if (match) {
        if (current) sections.push(current);
        current = { title: stripInlineMd(match[1]), body: [] };
        return;
      }
      if (!current) {
        current = { title: "", body: [line] };
        return;
      }
      current.body.push(line);
    });
  if (current) sections.push(current);
  return sections.filter((section) => section.title || section.body.join("\n").trim());
};

const resolveArchitectureLevel = (heading = "") => {
  if (/low\s*level/i.test(heading)) return "Low Level";
  if (/high\s*level/i.test(heading)) return "High Level";
  return "";
};

const parseDiagramBlock = (heading = "", parentTitle = "", body = "") => {
  const text = String(body || "");
  const titleMatch = text.match(/^\s*title\s*[:：]\s*(.+)$/im);
  const { charts, remainder } = extractMermaidBlocks(text);
  const classified = classifyMermaidCharts(charts);
  const chart = classified.architecture[0] || charts.find((item) => !/^erDiagram\b/i.test(item)) || "";
  const level = resolveArchitectureLevel(heading) || resolveArchitectureLevel(parentTitle);
  const composedTitle = stripInlineMd(titleMatch?.[1] || "")
    || [parentTitle, heading].filter(Boolean).join(" — ")
    || "Architecture";
  let collectingBreakdown = false;
  const breakdown = [];
  const notes = [];
  String(remainder)
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      if (/^title\s*[:：]/i.test(line)) return;
      if (/^#{1,6}\s/.test(line)) return;
      const cleaned = stripInlineMd(line).replace(/[:：]\s*$/, "");
      if (/^breakdown$/i.test(cleaned)) {
        collectingBreakdown = true;
        return;
      }
      if (collectingBreakdown && /^[-*•]/.test(line)) {
        breakdown.push(stripInlineMd(line.replace(/^[-*•]\s*/, "")));
        return;
      }
      if (collectingBreakdown && line.length < 160 && !/^#{1,6}/.test(line)) {
        breakdown.push(stripInlineMd(line.replace(/^[-*•]\s*/, "")));
        return;
      }
      collectingBreakdown = false;
      if (!isMermaidDebrisLine(line)) notes.push(stripInlineMd(line.replace(/^[-*•]\s*/, "")));
    });
  return {
    title: composedTitle,
    module: parentTitle || heading,
    level,
    chart,
    breakdown: breakdown.filter(Boolean),
    notes: notes.join("\n"),
  };
};

const parseArchitectureModules = (text = "") => {
  const h3 = splitMarkdownByHeading(text, 3).filter((section) => section.title);
  if (!h3.length) return [];
  const modules = [];
  h3.forEach((section) => {
    const body = section.body.join("\n");
    const h4 = splitMarkdownByHeading(body, 4).filter((item) => item.title);
    if (h4.length) {
      h4.forEach((sub) => {
        modules.push(parseDiagramBlock(sub.title, section.title, sub.body.join("\n")));
      });
      return;
    }
    modules.push(parseDiagramBlock(section.title, "", body));
  });
  return modules.filter((item) => item.title && (item.chart || item.breakdown.length || item.notes));
};

export const parsePlanMarkdownDocument = (content = "") => {
  const doc = emptyPlanDocument();
  if (!content || typeof content !== "string") return doc;

  const lines = content.split(/\n/);
  const sections = [];
  let current = { title: "overview", body: [] };

  lines.forEach((line) => {
    const h1 = line.match(/^#\s+(.+)$/);
    if (h1 && current.body.length === 0 && current.title === "overview") {
      return;
    }
    const h2 = line.match(/^##\s+(.+)$/);
    if (h2 && h2[1] && h2[1].length < 80) {
      sections.push(current);
      current = { title: h2[1].trim(), body: [] };
      return;
    }
    current.body.push(line);
  });
  sections.push(current);

  sections.forEach((section) => {
    const body = section.body.join("\n").trim();
    if (!body && !section.title) return;
    const alias = SECTION_ALIASES.find((item) => item.match.test(String(section.title || "").trim()));
    const key = alias?.key || null;
    if (!key) {
      if (body) doc.rawSections[section.title || "Notes"] = body;
      return;
    }
    doc.rawSections[key] = body;
    switch (key) {
      case "overview":
        doc.overview = stripInlineMd(body.replace(/\n{3,}/g, "\n\n"));
        break;
      case "objective":
        doc.objective = body;
        break;
      case "scope":
        doc.scope = parseScopeBlock(body);
        break;
      case "features":
        doc.features = bulletLines(body);
        break;
      case "users":
        doc.users = bulletLines(body);
        break;
      case "screens":
        doc.screens = bulletLines(body);
        break;
      case "architecture": {
        const parsed = parseArchitecture(body);
        doc.architectureMermaid = parsed.mermaid;
        doc.extraMermaid = parsed.extra;
        doc.architectureNotes = parsed.notes;
        doc.architecture = parsed.nodes;
        doc.architectureModules = parseArchitectureModules(body);
        if (parsed.erd && !doc.erdMermaid) doc.erdMermaid = parsed.erd;
        break;
      }
      case "erd": {
        const parsed = parseArchitecture(body);
        doc.erdMermaid = parsed.erd || parsed.mermaid;
        doc.erdNotes = parsed.notes;
        break;
      }
      case "techStack":
        doc.techStack = parseTechStack(body);
        break;
      case "agents": {
        const parsed = parseAgents(body);
        doc.agents = parsed.agents;
        doc.agentsNote = parsed.note;
        break;
      }
      case "milestones":
        doc.milestones = parseMilestones(body);
        break;
      case "security":
        doc.security = bulletLines(body);
        break;
      case "assumptions":
        doc.assumptions = bulletLines(body);
        break;
      case "risks":
        doc.risks = bulletLines(body);
        break;
      case "creditEstimation":
        doc.creditEstimation = body;
        break;
      default:
        break;
    }
  });

  if (!doc.overview && !doc.objective) {
    const firstParagraph = content.split(/\n\n+/).map((p) => p.trim()).find((p) => p && !/^#{1,6}/.test(p));
    if (firstParagraph && firstParagraph.length < 500) {
      doc.overview = stripInlineMd(firstParagraph);
    }
  }

  const globalCharts = classifyMermaidCharts(extractMermaidBlocks(content).charts);
  if (!doc.architectureMermaid && globalCharts.architecture[0]) {
    doc.architectureMermaid = globalCharts.architecture[0];
  }
  if (globalCharts.architecture.length > 1 || globalCharts.extra.length) {
    doc.extraMermaid = [...new Set([...(doc.extraMermaid || []), ...globalCharts.architecture.slice(1), ...globalCharts.extra])];
  }
  if (!doc.erdMermaid && globalCharts.erd[0]) {
    doc.erdMermaid = globalCharts.erd[0];
  }

  return doc;
};

const linesToList = (text = "") =>
  String(text || "")
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const listToLines = (items = []) => (items || []).join("\n");

const statusToLabel = (status = "pending") => {
  if (status === "completed") return "Completed";
  if (status === "current") return "In Progress";
  if (status === "testing") return "Testing";
  if (status === "failed") return "Failed";
  if (status === "blocked") return "Blocked";
  return "Pending";
};

export const planDocumentToDraft = (doc = emptyPlanDocument()) => ({
  overview: doc.overview || "",
  objective: doc.objective || "",
  scopeIncluded: listToLines(doc.scope?.included),
  scopeExcluded: listToLines(doc.scope?.excluded),
  features: listToLines(doc.features),
  users: listToLines(doc.users),
  screens: listToLines(doc.screens),
  architectureNotes: doc.architectureNotes || "",
  architectureMermaid: doc.architectureMermaid || "",
  erdNotes: doc.erdNotes || "",
  erdMermaid: doc.erdMermaid || "",
  techStack: (doc.techStack || [])
    .map((item) => (item.label ? `${item.label}: ${item.value || ""}` : item.value || ""))
    .filter(Boolean)
    .join("\n"),
  agentsNote: doc.agentsNote || "",
  agents: (doc.agents || []).map((agent) => ({
    name: agent.name || "",
    responsibility: agent.responsibility || "",
    tools: (agent.tools || []).join(", "),
  })),
  milestones: (doc.milestones || []).map((item) => ({
    title: item.title || "",
    status: item.status || "pending",
    tasks: listToLines(item.tasks),
  })),
  security: listToLines(doc.security),
  assumptions: listToLines(doc.assumptions),
  risks: listToLines(doc.risks),
  creditEstimation: doc.creditEstimation || "",
});

export const serializePlanDraftToMarkdown = (draft = {}, title = "Application Plan") => {
  const bullets = (text) =>
    linesToList(text)
      .map((line) => `- ${line}`)
      .join("\n");
  const mermaidBlock = (chart) => {
    const cleaned = String(chart || "").trim();
    return cleaned ? `\n\`\`\`mermaid\n${cleaned}\n\`\`\`\n` : "";
  };
  const agentsBlock = () => {
    const agents = (draft.agents || []).filter((agent) => String(agent.name || "").trim());
    if (agents.length) {
      return agents
        .map((agent) => {
          const tools = String(agent.tools || "").trim();
          const responsibility = String(agent.responsibility || "").trim();
          return `- **${agent.name.trim()}**${responsibility ? `: ${responsibility}` : ""}${
            tools ? ` — tools: ${tools}` : ""
          }`;
        })
        .join("\n");
    }
    return String(draft.agentsNote || "").trim() || "This application does not include AI agents.";
  };
  const implementation = (draft.milestones || [])
    .map((item, index) => {
      const number = String(index + 1).padStart(2, "0");
      const heading = String(item.title || "").trim() || `Phase ${index + 1}`;
      const tasks = linesToList(item.tasks)
        .map((task) => `- ${task}`)
        .join("\n");
      return `### ${number}. ${heading}\nStatus: ${statusToLabel(item.status)}\n${tasks}`.trim();
    })
    .join("\n\n");

  return [
    `# ${title}`,
    "",
    "## Overview",
    String(draft.overview || "").trim(),
    "",
    "## Objective",
    String(draft.objective || "").trim(),
    "",
    "## Scope",
    "### Included",
    bullets(draft.scopeIncluded),
    "### Excluded",
    bullets(draft.scopeExcluded),
    "",
    "## Key Features",
    bullets(draft.features),
    "",
    "## Users & Roles",
    bullets(draft.users),
    "",
    "## Screens / Pages & User Flows",
    bullets(draft.screens),
    "",
    "## Architecture",
    String(draft.architectureNotes || "").trim(),
    mermaidBlock(draft.architectureMermaid),
    "",
    "## Database / ERD",
    mermaidBlock(draft.erdMermaid),
    String(draft.erdNotes || "").trim(),
    "",
    "## Agents / AI Components",
    agentsBlock(),
    "",
    "## Technology Stack",
    bullets(draft.techStack),
    "",
    "## Implementation Plan",
    implementation,
    "",
    "## Security Considerations",
    bullets(draft.security),
    "",
    "## Assumptions",
    bullets(draft.assumptions),
    "",
    "## Risks / Considerations",
    bullets(draft.risks),
    "",
    "## Credit Estimation",
    String(draft.creditEstimation || "").trim(),
  ]
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const normalizePlanDocument = (raw) => {
  if (!raw) return emptyPlanDocument();
  if (typeof raw === "string") {
    const parsed = tryParseJson(raw);
    if (parsed && typeof parsed === "object") return normalizePlanDocument(parsed);
    return parsePlanMarkdownDocument(raw);
  }
  if (typeof raw !== "object") return emptyPlanDocument();

  const markdownSource = raw.planText || raw.plan_text || raw.markdown;
  const fromMarkdown =
    typeof markdownSource === "string" && markdownSource.trim()
      ? parsePlanMarkdownDocument(
          String(markdownSource)
            .replace(/^\s*BEGIN_PLAN\s*/i, "")
            .replace(/\s*END_PLAN\s*$/i, "")
            .trim()
        )
      : emptyPlanDocument();

  const base = fromMarkdown;
  base.overview = raw.overview || raw.summary || raw.projectOverview || base.overview;
  base.objective = raw.objective || raw.goal || raw.goals || base.objective;
  if (Array.isArray(raw.scope)) {
    base.scope = { included: raw.scope, excluded: [] };
  } else if (raw.scope && typeof raw.scope === "object") {
    base.scope = {
      included: asArray(raw.scope.included || raw.scope.inScope || raw.scope.includes),
      excluded: asArray(raw.scope.excluded || raw.scope.outOfScope || raw.scope.excludes),
    };
  }
  const mappedFeatures = asArray(raw.features || raw.keyFeatures || raw.featureList).map((item) =>
    typeof item === "string" ? item : item?.name || item?.title || item?.label || ""
  ).filter(Boolean);
  if (mappedFeatures.length) base.features = mappedFeatures;
  const mappedUsers = asArray(raw.users || raw.roles || raw.usersAndRoles).map((item) =>
    typeof item === "string" ? item : item?.name || item?.role || item?.title || item?.label || ""
  ).filter(Boolean);
  if (mappedUsers.length) base.users = mappedUsers;
  const mappedScreens = asArray(raw.screens || raw.pages || raw.flows).map((item) =>
    typeof item === "string" ? item : item?.name || item?.title || item?.path || item?.label || ""
  ).filter(Boolean);
  if (mappedScreens.length) base.screens = mappedScreens;
  const mappedArchitecture = asArray(raw.architecture || raw.architectureFlow || raw.layers).map((item) =>
    typeof item === "string" ? item : item?.name || item?.label || ""
  ).filter(Boolean);
  if (mappedArchitecture.length) base.architecture = mappedArchitecture;
  base.architectureMermaid = raw.architectureMermaid || raw.architecture_mermaid || base.architectureMermaid;
  base.architectureNotes = raw.architectureNotes || raw.architecture_notes || base.architectureNotes;
  if (Array.isArray(raw.architectureModules) && raw.architectureModules.length) {
    base.architectureModules = raw.architectureModules;
  }
  const mappedExtra = asArray(raw.extraMermaid || raw.extra_mermaid).filter(Boolean);
  if (mappedExtra.length) base.extraMermaid = mappedExtra;
  base.erdMermaid = raw.erdMermaid || raw.erd_mermaid || (typeof raw.erd === "string" ? raw.erd : "") || base.erdMermaid;
  base.erdNotes = raw.erdNotes || raw.erd_notes || base.erdNotes;
  base.agentsNote = raw.agentsNote || raw.agents_note || base.agentsNote;
  const mappedStack = asArray(raw.techStack || raw.technologyStack || raw.stack).map((item) => {
    if (typeof item === "string") return { label: "Tech", value: item };
    return {
      label: item.label || item.name || item.category || "Tech",
      value: item.value || item.technology || item.tech || item.name || "",
    };
  }).filter((item) => item.value);
  if (mappedStack.length) base.techStack = mappedStack;
  const mappedAgents = asArray(raw.agents || raw.aiComponents).map((item, index) => {
    if (typeof item === "string") return { id: `agent-${index}`, name: item, responsibility: "", tools: [] };
    return {
      id: item.id || `agent-${index}`,
      name: item.name || item.title || `Agent ${index + 1}`,
      responsibility: item.responsibility || item.role || item.description || "",
      tools: asArray(item.tools),
    };
  });
  if (mappedAgents.length) base.agents = mappedAgents;
  const mappedMilestones = asArray(raw.milestones || raw.implementationPlan || raw.timeline || raw.tasks).map((item, index) => {
    if (typeof item === "string") return { id: `m-${index}`, title: item, detail: "", tasks: [], status: "pending" };
    return {
      id: item.id || `m-${index}`,
      title: item.title || item.name || item.label || `Step ${index + 1}`,
      detail: item.detail || item.description || "",
      tasks: asArray(item.tasks),
      status: normalizeTaskStatus(item.status || "pending"),
    };
  });
  if (mappedMilestones.length) base.milestones = mappedMilestones;
  const mappedSecurity = asArray(raw.security || raw.securityConsiderations).map((item) => (typeof item === "string" ? item : item?.text || item?.label || "")).filter(Boolean);
  if (mappedSecurity.length) base.security = mappedSecurity;
  const mappedAssumptions = asArray(raw.assumptions).map((item) => (typeof item === "string" ? item : item?.text || item?.label || "")).filter(Boolean);
  if (mappedAssumptions.length) base.assumptions = mappedAssumptions;
  const mappedRisks = asArray(raw.risks || raw.considerations).map((item) => (typeof item === "string" ? item : item?.text || item?.label || "")).filter(Boolean);
  if (mappedRisks.length) base.risks = mappedRisks;
  const creditRaw =
    raw.creditEstimation ||
    raw.credit_estimation ||
    raw.credits ||
    raw.rawSections?.creditEstimation ||
    raw.rawSections?.["Credit Estimation"];
  if (typeof creditRaw === "string" && creditRaw.trim()) {
    base.creditEstimation = creditRaw.trim();
  }
  return base;
};

export const mergePlanDocuments = (...docs) => {
  const result = emptyPlanDocument();
  docs.filter(Boolean).forEach((doc) => {
    const normalized = normalizePlanDocument(doc);
    if (normalized.overview) result.overview = normalized.overview;
    if (normalized.objective) result.objective = normalized.objective;
    if (normalized.scope?.included?.length) result.scope.included = normalized.scope.included;
    if (normalized.scope?.excluded?.length) result.scope.excluded = normalized.scope.excluded;
    if (normalized.features?.length) result.features = normalized.features;
    if (normalized.users?.length) result.users = normalized.users;
    if (normalized.screens?.length) result.screens = normalized.screens;
    if (normalized.architecture?.length) result.architecture = normalized.architecture;
    if (normalized.architectureMermaid) result.architectureMermaid = normalized.architectureMermaid;
    if (normalized.architectureNotes) result.architectureNotes = normalized.architectureNotes;
    if (normalized.architectureModules?.length) result.architectureModules = normalized.architectureModules;
    if (normalized.extraMermaid?.length) result.extraMermaid = normalized.extraMermaid;
    if (normalized.erdMermaid) result.erdMermaid = normalized.erdMermaid;
    if (normalized.erdNotes) result.erdNotes = normalized.erdNotes;
    if (normalized.techStack?.length) result.techStack = normalized.techStack;
    if (normalized.agents?.length) result.agents = normalized.agents;
    if (normalized.agentsNote) result.agentsNote = normalized.agentsNote;
    if (normalized.milestones?.length) result.milestones = normalized.milestones;
    if (normalized.security?.length) result.security = normalized.security;
    if (normalized.assumptions?.length) result.assumptions = normalized.assumptions;
    if (normalized.risks?.length) result.risks = normalized.risks;
    if (normalized.creditEstimation) result.creditEstimation = normalized.creditEstimation;
  });
  return result;
};

export const extractPlanDocumentFromMessage = (message) => {
  if (!message || typeof message !== "object") return null;
  const structured =
    message.planDocument ||
    message.plan_document ||
    message.planData ||
    message.plan_data ||
    message.metadata?.planDocument ||
    message.metadata?.planData;
  if (structured) return normalizePlanDocument(structured);
  if (isPlanQuestionMessage(message)) return null;
  const markdown = message.planText || message.content;
  if (typeof markdown === "string" && markdown.trim()) {
    const cleaned = markdown
      .replace(/^\s*BEGIN_PLAN\s*/i, "")
      .replace(/\s*END_PLAN\s*$/i, "")
      .trim();
    return parsePlanMarkdownDocument(cleaned);
  }
  return null;
};

export const extractPlanDocumentFromWs = (message) => {
  if (!message || typeof message !== "object") return null;
  const ninfo = message.ninfo && typeof message.ninfo === "object" ? message.ninfo : {};
  const planEvent = String(ninfo.plan_event || ninfo.planEvent || message.plan_event || "").toLowerCase();
  if (planEvent === "questions") return null;
  const candidates = [
    ninfo.planDocument,
    ninfo.plan_document,
    ninfo.planData,
    ninfo.plan_data,
    ninfo.metadata?.planDocument,
    message.planDocument,
    message.planData,
  ];
  for (const candidate of candidates) {
    if (candidate) return normalizePlanDocument(candidate);
  }
  const planText = ninfo.planText || ninfo.plan_text || message.planText;
  const assistantMsg = ninfo.assistantMsg || message.assistantMsg;
  const markdown = planText || assistantMsg;
  const looksLikePlan =
    Boolean(planText) ||
    (typeof markdown === "string" &&
      (/BEGIN_PLAN/i.test(markdown) || /^#{1,2}\s+/m.test(markdown)));
  if (typeof markdown === "string" && looksLikePlan) {
    const cleaned = markdown
      .replace(/^\s*BEGIN_PLAN\s*/i, "")
      .replace(/\s*END_PLAN\s*$/i, "")
      .trim();
    return parsePlanMarkdownDocument(cleaned);
  }
  return null;
};

export const buildPlanDocumentFromMessages = (messages = []) => {
  // Latest complete plan wins — do not field-merge older plans into a revision
  // (that left stale overview/architecture from plan vN when vN+1 arrived).
  let latest = emptyPlanDocument();
  (messages || []).forEach((msg) => {
    const extracted = extractPlanDocumentFromMessage(msg);
    if (extracted && hasMeaningfulPlanDocument(extracted)) {
      latest = extracted;
    }
  });
  return latest;
};

export const getPlanWorkspaceProgress = (doc = emptyPlanDocument()) => {
  const steps = [
    { key: "overview", label: "Understanding requirements", done: Boolean(doc.overview || doc.objective) },
    { key: "features", label: "Identifying features", done: (doc.features || []).length > 0 || (doc.scope?.included || []).length > 0 },
    { key: "architecture", label: "Designing architecture", done: Boolean(doc.architectureMermaid) || (doc.architectureModules || []).length > 0 || (doc.architecture || []).length > 0 },
    { key: "erd", label: "Designing database", done: Boolean(doc.erdMermaid) || (doc.techStack || []).some((item) => /database/i.test(item.label || "")) },
    { key: "milestones", label: "Preparing implementation plan", done: (doc.milestones || []).length > 0 },
    { key: "credits", label: "Estimating build credits", done: Boolean(doc.creditEstimation) },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const currentIndex = steps.findIndex((s) => !s.done);
  return {
    steps: steps.map((step, index) => ({
      ...step,
      state: step.done ? "done" : index === (currentIndex < 0 ? steps.length - 1 : currentIndex) ? "current" : "pending",
    })),
    percent: Math.round((doneCount / steps.length) * 100),
    isComplete: doneCount === steps.length,
  };
};

export const hasMeaningfulPlanDocument = (doc) => {
  if (!doc) return false;
  return Boolean(
    doc.overview ||
      doc.objective ||
      (doc.features || []).length ||
      (doc.architecture || []).length ||
      doc.architectureMermaid ||
      (doc.architectureModules || []).length ||
      doc.erdMermaid ||
      (doc.techStack || []).length ||
      (doc.milestones || []).length ||
      (doc.scope?.included || []).length
  );
};

export const toPlanDtoMessage = ({
  id,
  role = "assistant",
  content = "",
  type = projectType.PLAN,
  planMessage,
  createdAt,
} = {}) => {
  const message = {
    role,
    content: content ?? "",
    type,
    createdAt: createdAt ? new Date(createdAt).toISOString() : new Date().toISOString(),
  };
  if (id != null && id !== "" && !String(id).startsWith("plan-")) {
    message.id = String(id);
  }
  if (planMessage != null) {
    message.planMessage = planMessage;
  }
  return message;
};

export const buildPlanRequestBody = ({
  prompt,
  projectId,
  planAction: action = planAction.START,
  answers,
  additionalInput,
  messages,
  planId,
  planText,
  plan,
} = {}) => {
  const body = {
    type: projectType.PLAN,
    metadata: {
      planAction: action,
    },
  };
  if (action === planAction.RESPOND) {
    body.prompt = null;
  } else if (action === planAction.UPDATE) {
    body.prompt = prompt || "Updated the project plan.";
  } else if (action === planAction.REFINE) {
    body.prompt = prompt || additionalInput || "";
  } else {
    body.prompt = prompt || "";
  }
  if (projectId != null && projectId !== "") {
    body.projectId = projectId;
  }
  if (answers != null) {
    body.metadata.answers = answers;
  }
  if (action === planAction.RESPOND) {
    body.metadata.additionalInput = additionalInput != null ? String(additionalInput) : "";
  } else if (action === planAction.REFINE) {
    body.metadata.additionalInput = String(additionalInput || prompt || "").trim();
  } else if (additionalInput != null && String(additionalInput).trim() !== "") {
    body.metadata.additionalInput = String(additionalInput).trim();
  }
  if (action === planAction.UPDATE) {
    if (planId) body.metadata.planId = planId;
    if (planText != null) body.metadata.planText = planText;
    if (plan) body.metadata.plan = plan;
  }
  if (action === planAction.REFINE) {
    if (planId) body.metadata.planId = planId;
    if (plan) body.metadata.plan = plan;
  }
  if (Array.isArray(messages) && messages.length > 0) {
    body.messages = messages;
  }
  return body;
};

export const appendProjectJsonPart = (formData, body) => {
  formData.append(
    "project",
    new Blob([JSON.stringify(body)], { type: "application/json" })
  );
  return formData;
};

/** True when the project already has a plan the user can refine from chat. */
export const hasReadyLivingPlan = (projectId, chatMessages = []) => {
  const meta = readPlanMeta(projectId) || {};
  const fromMsg = [...(chatMessages || [])]
    .reverse()
    .find((msg) => msg?.planId || msg?.planText || msg?.buildAvailable);
  const planId = meta.planId || fromMsg?.planId || null;
  const planText = String(meta.planText || fromMsg?.planText || "").trim();
  const doc = buildPlanDocumentFromMessages(chatMessages);
  const hasDoc = hasMeaningfulPlanDocument(doc);
  return Boolean(
    planId &&
      (meta.buildAvailable === true ||
        fromMsg?.buildAvailable === true ||
        String(meta.planStatus || "").toLowerCase() === "ready" ||
        planText ||
        hasDoc)
  );
};

/** Plan chat submit: START for new plans, REFINE when a living plan already exists. */
export const buildPlanChatSubmitBody = ({ prompt, projectId, chatMessages = [] } = {}) => {
  const meta = readPlanMeta(projectId) || {};
  const fromMsg = [...(chatMessages || [])]
    .reverse()
    .find((msg) => msg?.planId || msg?.planText);
  const planId = meta.planId || fromMsg?.planId || null;
  let planText = String(meta.planText || fromMsg?.planText || "").trim();
  if (!planText) {
    const doc = buildPlanDocumentFromMessages(chatMessages);
    if (hasMeaningfulPlanDocument(doc)) {
      planText = serializePlanDraftToMarkdown(doc, doc.title || "Application Plan");
    }
  }
  if (hasReadyLivingPlan(projectId, chatMessages) && planId && prompt?.trim()) {
    return {
      refining: true,
      body: buildPlanRequestBody({
        prompt,
        projectId,
        planAction: planAction.REFINE,
        additionalInput: prompt,
        planId,
        planText,
        plan: { id: planId, planText, format: "markdown" },
      }),
    };
  }
  return {
    refining: false,
    body: buildPlanRequestBody({
      prompt,
      projectId,
      planAction: planAction.START,
    }),
  };
};

export const hydratePlanChatMessage = (message, successStatus = "success", projectId) => {
  if (!message || typeof message !== "object") return message;
  if (message.role === "user") return message;

  if (isPlanType(message) && !isPlanQuestionMessage(message) && !Array.isArray(message.planQuestions)) {
    return {
      ...message,
      type: PLAN_APP_TYPE,
      jobStatus: message.jobStatus || successStatus,
    };
  }

  const parsed =
    parsePlanMessage(readPlanRaw(message)) ||
    (isPlanType(message) ? parsePlanMessage(message.content) : null);

  if (!parsed && !(Array.isArray(message.planQuestions) && message.planQuestions.length)) {
    return message;
  }

  const questions = parsed?.questions || message.planQuestions || [];
  if (!questions.length) return message;

  const fingerprint = message.planFingerprint || getQuestionIdFingerprint(questions);
  const stored = readPlanProgress(projectId, { ...message, planQuestions: questions, planFingerprint: fingerprint });
  const persistedAnswers = sanitizePlanAnswers(questions, {
    ...stored.answers,
    ...extractPersistedAnswers(message),
  });
  const additionalInput = message.planAdditionalInput || stored.additionalInput || "";
  const rawContent = typeof message.content === "string" ? message.content : "";
  const constructedContent = /^\s*Question:/i.test(rawContent);
  const intro =
    parsed?.intro ||
    message.planIntro ||
    (constructedContent ? "" : rawContent) ||
    "";
  const hasValidAnswers = Object.keys(persistedAnswers).length > 0;

  return {
    ...message,
    id: message.id,
    role: "assistant",
    type: PLAN_APP_TYPE,
    messageKind: PLAN_MESSAGE_KIND,
    jobStatus: message.jobStatus || successStatus,
    content: intro,
    planIntro: intro || "Let's understand your requirements before we start building.",
    planMessage: message.planMessage || questions,
    planQuestions: questions,
    planFingerprint: fingerprint,
    planStatus:
      message.planStatus === "submitted" && hasValidAnswers
        ? "submitted"
        : stored.submitted && hasValidAnswers
        ? "submitted"
        : constructedContent && hasValidAnswers
        ? "submitted"
        : "active",
    planAnswers: persistedAnswers,
    planAdditionalInput: additionalInput,
    title: message.title || "SuperAgentX AI",
  };
};

export const restorePlanConversationState = (messages = [], projectId) => {
  const restored = (messages || []).map((msg) => hydratePlanChatMessage(msg, msg.jobStatus, projectId));
  return restored.map((msg, index) => {
    if (!isPlanQuestionMessage(msg)) return msg;
    const laterComplete = restored.slice(index + 1).some((next) => isCompletedPlanResultMessage(next));
    const stored = readPlanProgress(projectId, msg);
    const answers = sanitizePlanAnswers(msg.planQuestions || [], {
      ...stored.answers,
      ...extractPersistedAnswers(msg),
      ...(msg.planAnswers || {}),
    });
    const hasValidAnswers = Object.keys(answers).length > 0;
    const submitted =
      (laterComplete || stored.submitted || msg.planStatus === "submitted") && hasValidAnswers;
    return {
      ...msg,
      planAnswers: answers,
      planAdditionalInput: hasValidAnswers
        ? msg.planAdditionalInput || stored.additionalInput || ""
        : "",
      planStatus: submitted ? "submitted" : "active",
    };
  });
};
